<template>
  <Layout>
    <Head title="Profile - Kiosk" />

    <div class="p-4 sm:p-6 md:p-8">
      <div class="space-y-6">
        <div class="border-b pb-4">
          <h1 class="text-2xl font-bold text-gray-900">{{ t('profile.settings') }}</h1>

          <p class="text-gray-600 mt-1">{{ t('profile.update_contact') }}</p>
        </div>

        <!-- Pending Changes Alert -->
        <Alert v-if="hasPendingChanges" class="border-amber-200 bg-amber-50">
          <AlertDescription class="text-amber-800">
            {{ t('profile.pending_changes_notice') }}
          </AlertDescription>
        </Alert>

        <form @submit="onSubmit" class="space-y-8">
          <!-- Email Addresses Section -->
          <Card>
            <CardHeader>
              <CardTitle>{{ t('profile.email_addresses') }}</CardTitle>
              <CardDescription>{{ t('profile.email_communication') }}</CardDescription>
            </CardHeader>

            <CardContent>
              <div class="space-y-4">
                <div
                  v-for="(email, index) in emails"
                  :key="email.id"
                  class="border rounded-lg p-4 space-y-4"
                >
                  <div class="flex items-center justify-between">
                    <h4 class="font-medium text-sm">{{ email.primary ? t('profile.primary_email') : t('profile.email') }}</h4>
                    <div class="flex items-center gap-2">
                      <Badge v-if="email.primary" variant="outline">{{ t('profile.primary') }}</Badge>
                      <FormField v-if="!email.primary" v-slot="{ componentField }" :name="`emails.${index}.primary`">
                        <FormItem class="flex items-center space-x-2">
                          <FormControl>
                            <input
                              type="radio"
                              :value="true"
                              :checked="email.primary"
                              @change="setPrimaryEmail(index)"
                              v-bind="componentField"
                              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                          </FormControl>
                          <FormLabel class="text-sm font-normal cursor-pointer">
                            {{ t('profile.set_as_primary') }}
                          </FormLabel>
                        </FormItem>
                      </FormField>
                    </div>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <PendingValueDisplay
                      :label="t('profile.email_address')"
                      :current-value="email.address"
                      :pending-value="email.pending_address"
                    >
                      <template v-slot="{ label }">
                        <FormField v-slot="{ componentField }" :name="`emails.${index}.address`">
                          <FormItem>
                            <FormLabel>{{ label }}</FormLabel>
                            <FormControl>
                              <Input
                                type="email"
                                :placeholder="t('profile.enter_email')"
                                v-bind="componentField"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        </FormField>
                      </template>
                    </PendingValueDisplay>

                    <PendingValueDisplay
                      :label="t('profile.owner')"
                      :current-value="email.owner"
                      :pending-value="email.pending_owner"
                    >
                      <template v-slot="{ label }">
                        <FormField v-slot="{ componentField }" :name="`emails.${index}.owner`">
                          <FormItem>
                            <FormLabel>{{ label }}</FormLabel>
                            <FormControl>
                              <Input
                                type="text"
                                :placeholder="t('profile.enter_owner')"
                                v-bind="componentField"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        </FormField>
                      </template>
                    </PendingValueDisplay>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <!-- Phone Numbers Section -->
          <Card>
            <CardHeader>
              <CardTitle>{{ t('profile.phone_numbers') }}</CardTitle>
              <CardDescription>{{ t('profile.phone_contact') }}</CardDescription>
            </CardHeader>

            <CardContent>
              <div class="space-y-4">
                <div
                  v-for="(phone, index) in phones"
                  :key="phone.id"
                  class="border rounded-lg p-4 space-y-4"
                >
                  <h4 class="font-medium text-sm">{{ t('profile.phone') }} {{ index + 1 }}</h4>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <PendingValueDisplay
                      :label="t('profile.owner')"
                      :current-value="phone.owner"
                      :pending-value="phone.pending_owner"
                    >
                      <template v-slot="{ label }">
                        <FormField v-slot="{ componentField }" :name="`phones.${index}.owner`">
                          <FormItem>
                            <FormLabel>{{ label }}</FormLabel>
                            <FormControl>
                              <Input
                                type="text"
                                :placeholder="t('profile.enter_owner')"
                                v-bind="componentField"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        </FormField>
                      </template>
                    </PendingValueDisplay>

                    <PendingValueDisplay
                      :label="t('profile.country_code')"
                      :current-value="phone.country_prefix"
                      :pending-value="phone.pending_country_prefix"
                    >
                      <template v-slot="{ label }">
                        <FormField v-slot="{ componentField }" :name="`phones.${index}.country_prefix`">
                          <FormItem>
                            <FormLabel>{{ label }}</FormLabel>
                            <FormControl>
                              <Input
                                type="text"
                                :placeholder="t('profile.country_prefix_placeholder')"
                                v-bind="componentField"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        </FormField>
                      </template>
                    </PendingValueDisplay>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <PendingValueDisplay
                      :label="t('profile.phone_number')"
                      :current-value="phone.phone_number"
                      :pending-value="phone.pending_phone_number"
                    >
                      <template v-slot="{ label }">
                        <FormField v-slot="{ componentField }" :name="`phones.${index}.phone_number`">
                          <FormItem>
                            <FormLabel>{{ label }}</FormLabel>
                            <FormControl>
                              <Input
                                type="tel"
                                :placeholder="t('profile.enter_phone')"
                                v-bind="componentField"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        </FormField>
                      </template>
                    </PendingValueDisplay>

                    <PendingValueDisplay
                      :label="t('profile.phone_type')"
                      :current-value="phone.phone_type"
                      :pending-value="phone.pending_phone_type"
                      :value-formatter="(value) => phoneTypes.find(pt => pt.value === value)?.text || value"
                    >
                      <template v-slot="{ label }">
                        <FormField v-slot="{ componentField }" :name="`phones.${index}.phone_type`">
                          <FormItem>
                            <FormLabel>{{ label }}</FormLabel>
                            <Select v-bind="componentField">
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue :placeholder="t('profile.select_phone_type')" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem
                                  v-for="phoneType in phoneTypes"
                                  :key="phoneType.value"
                                  :value="phoneType.value"
                                >
                                  {{ phoneType.text }}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        </FormField>
                      </template>
                    </PendingValueDisplay>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <!-- Physical Address Section -->
          <Card>
            <CardHeader>
              <CardTitle>{{ t('profile.physical_address') }}</CardTitle>

              <CardDescription>{{ t('profile.current_residential') }}</CardDescription>
            </CardHeader>

            <CardContent>
              <div class="space-y-4">
                <PendingValueDisplay
                  :label="t('profile.street_address')"
                  :current-value="user.addresses.physical.address"
                  :pending-value="user.addresses.physical.pending_address"
                >
                  <template v-slot="{ label }">
                    <FormField v-slot="{ componentField }" name="addresses.physical.address">
                      <FormItem>
                        <FormLabel>{{ label }}</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            :placeholder="t('profile.enter_street_address')"
                            v-bind="componentField"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    </FormField>
                  </template>
                </PendingValueDisplay>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <PendingValueDisplay
                    :label="t('profile.city')"
                    :current-value="user.addresses.physical.city"
                    :pending-value="user.addresses.physical.pending_city"
                  >
                    <template v-slot="{ label }">
                      <FormField v-slot="{ componentField }" name="addresses.physical.city">
                        <FormItem>
                          <FormLabel>{{ label }}</FormLabel>
                          <FormControl>
                            <Input
                              type="text"
                              :placeholder="t('profile.enter_city')"
                              v-bind="componentField"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      </FormField>
                    </template>
                  </PendingValueDisplay>

                  <PendingValueDisplay
                    :label="t('profile.state')"
                    :current-value="user.addresses.physical.state"
                    :pending-value="user.addresses.physical.pending_state"
                  >
                    <template v-slot="{ label }">
                      <FormField v-slot="{ componentField }" name="addresses.physical.state">
                        <FormItem>
                          <FormLabel>{{ label }}</FormLabel>
                          <FormControl>
                            <Input
                              type="text"
                              :placeholder="t('profile.enter_state')"
                              v-bind="componentField"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      </FormField>
                    </template>
                  </PendingValueDisplay>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <PendingValueDisplay
                    :label="t('profile.zip_code')"
                    :current-value="user.addresses.physical.zip"
                    :pending-value="user.addresses.physical.pending_zip"
                  >
                    <template v-slot="{ label }">
                      <FormField v-slot="{ componentField }" name="addresses.physical.zip">
                        <FormItem>
                          <FormLabel>{{ label }}</FormLabel>
                          <FormControl>
                            <Input
                              type="text"
                              inputmode="numeric"
                              pattern="[0-9]*"
                              maxlength="5"
                              :placeholder="t('profile.enter_zip')"
                              v-bind="componentField"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      </FormField>
                    </template>
                  </PendingValueDisplay>

                  <PendingValueDisplay
                    :label="t('profile.county')"
                    :current-value="user.addresses.physical.county"
                    :pending-value="user.addresses.physical.pending_county"
                  >
                    <template v-slot="{ label }">
                      <FormField v-slot="{ componentField }" name="addresses.physical.county">
                        <FormItem>
                          <FormLabel>{{ label }}</FormLabel>
                          <FormControl>
                            <Input
                              type="text"
                              :placeholder="t('profile.enter_county')"
                              :readonly="isPhysicalCountyReadonly"
                              v-bind="componentField"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      </FormField>
                    </template>
                  </PendingValueDisplay>
                </div>

                <PendingValueDisplay
                  :label="t('profile.country')"
                  :current-value="user.addresses.physical.country_id"
                  :pending-value="user.addresses.physical.pending_country_id"
                  :value-formatter="(value) => countries.find(c => c.id === value)?.name || value"
                >
                  <template v-slot="{ label }">
                    <FormField v-slot="{ componentField }" name="addresses.physical.country_id">
                      <FormItem>
                        <FormLabel>{{ label }}</FormLabel>
                        <Select v-bind="componentField">
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue :placeholder="t('profile.select_country')" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem v-for="country in countries" :key="country.id" :value="country.id">
                              {{ country.name }}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    </FormField>
                  </template>
                </PendingValueDisplay>
              </div>
            </CardContent>
          </Card>

          <!-- Mailing Address Section -->
          <Card>
            <CardHeader>
              <CardTitle>{{ t('profile.mailing_address') }}</CardTitle>

              <CardDescription>{{ t('profile.mailing_description') }}</CardDescription>
            </CardHeader>

            <CardContent>
              <div class="space-y-4">
                <PendingValueDisplay
                  :label="t('profile.same_as_physical')"
                  :current-value="user.addresses.mailing.same_as_physical"
                  :pending-value="user.addresses.mailing.pending_same_as_physical"
                  :value-formatter="(value) => value ? t('profile.yes') : t('profile.no')"
                >
                  <template v-slot="{ label }">
                    <FormField v-slot="{ value }" name="addresses.mailing.same_as_physical">
                      <FormItem>
                        <div class="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              :model-value="value"
                              @update:model-value="(checked) => setFieldValue('addresses.mailing.same_as_physical', checked)"
                              class="text-gray-600"
                            />
                          </FormControl>
                          <div class="space-y-1 leading-none">
                            <FormLabel class="text-sm font-medium leading-none">
                              {{ label }}
                            </FormLabel>
                            <p class="text-sm text-muted-foreground">
                              {{ t('profile.use_same_address') }}
                            </p>
                          </div>
                        </div>
                        <FormMessage />
                      </FormItem>
                    </FormField>
                  </template>
                </PendingValueDisplay>

                <div
                  v-if="showMailingAddressFields"
                  class="space-y-4"
                >
                  <PendingValueDisplay
                    :label="t('profile.street_address')"
                    :current-value="user.addresses.mailing.address"
                    :pending-value="user.addresses.mailing.pending_address"
                  >
                    <template v-slot="{ label }">
                      <FormField v-slot="{ componentField }" name="addresses.mailing.address">
                        <FormItem>
                          <FormLabel>{{ label }}</FormLabel>
                          <FormControl>
                            <Input
                              type="text"
                              :placeholder="t('profile.enter_mailing_address')"
                              v-bind="componentField"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      </FormField>
                    </template>
                  </PendingValueDisplay>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <PendingValueDisplay
                      :label="t('profile.city')"
                      :current-value="user.addresses.mailing.city"
                      :pending-value="user.addresses.mailing.pending_city"
                    >
                      <template v-slot="{ label }">
                        <FormField v-slot="{ componentField }" name="addresses.mailing.city">
                          <FormItem>
                            <FormLabel>{{ label }}</FormLabel>
                            <FormControl>
                              <Input
                                type="text"
                                :placeholder="t('profile.enter_city')"
                                v-bind="componentField"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        </FormField>
                      </template>
                    </PendingValueDisplay>

                    <PendingValueDisplay
                      :label="t('profile.state')"
                      :current-value="user.addresses.mailing.state"
                      :pending-value="user.addresses.mailing.pending_state"
                    >
                      <template v-slot="{ label }">
                        <FormField v-slot="{ componentField }" name="addresses.mailing.state">
                          <FormItem>
                            <FormLabel>{{ label }}</FormLabel>
                            <FormControl>
                              <Input
                                type="text"
                                :placeholder="t('profile.enter_state')"
                                v-bind="componentField"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        </FormField>
                      </template>
                    </PendingValueDisplay>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <PendingValueDisplay
                      :label="t('profile.zip_code')"
                      :current-value="user.addresses.mailing.zip"
                      :pending-value="user.addresses.mailing.pending_zip"
                    >
                      <template v-slot="{ label }">
                        <FormField v-slot="{ componentField }" name="addresses.mailing.zip">
                          <FormItem>
                            <FormLabel>{{ label }}</FormLabel>
                            <FormControl>
                              <Input
                                type="text"
                                inputmode="numeric"
                                pattern="[0-9]*"
                                maxlength="5"
                                :placeholder="t('profile.enter_zip')"
                                v-bind="componentField"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        </FormField>
                      </template>
                    </PendingValueDisplay>

                    <PendingValueDisplay
                      :label="t('profile.county')"
                      :current-value="user.addresses.mailing.county"
                      :pending-value="user.addresses.mailing.pending_county"
                    >
                      <template v-slot="{ label }">
                        <FormField v-slot="{ componentField }" name="addresses.mailing.county">
                          <FormItem>
                            <FormLabel>{{ label }}</FormLabel>
                            <FormControl>
                              <Input
                                type="text"
                                :placeholder="t('profile.enter_county')"
                                :readonly="isMailingCountyReadonly"
                                v-bind="componentField"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        </FormField>
                      </template>
                    </PendingValueDisplay>
                  </div>

                  <PendingValueDisplay
                    :label="t('profile.country')"
                    :current-value="user.addresses.mailing.country_id"
                    :pending-value="user.addresses.mailing.pending_country_id"
                    :value-formatter="(value) => countries.find(c => c.id === value)?.name || value"
                  >
                    <template v-slot="{ label }">
                      <FormField v-slot="{ componentField }" name="addresses.mailing.country_id">
                        <FormItem>
                          <FormLabel>{{ label }}</FormLabel>
                          <Select v-bind="componentField">
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue :placeholder="t('profile.select_country')" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem v-for="country in countries" :key="country.id" :value="country.id">
                                {{ country.name }}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      </FormField>
                    </template>
                  </PendingValueDisplay>
                </div>
              </div>
            </CardContent>
          </Card>

          <div class="flex justify-end">
            <LoadingButton
              v-if="form.processing"
              type="submit"
            />

            <Button
              v-else
              type="submit"
            >
              {{ t('profile.update_profile') }}
            </Button>
          </div>
        </form>
      </div>
    </div>
  </Layout>
  <Toaster />
</template>

<script setup>
import { Head, useForm } from '@inertiajs/vue3'
import { computed, ref, watch } from 'vue'
import { toTypedSchema } from '@vee-validate/zod'
import { useForm as useVeeForm } from 'vee-validate'
import * as z from 'zod'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import Layout from '@/Layouts/Minimal.vue'
import { LoadingButton } from '@/components/ui/loading-button'
import PendingValueDisplay from '@/components/ui/PendingValueDisplay.vue'
import { Toaster } from '@/components/ui/toast'
import { toast } from '@/components/ui/toast/use-toast'
import { t } from '@/entrypoints/translations.js'

const props = defineProps({
  countries: { type: Array, default: () => [] },
  phone_types: { type: Array, default: () => [] },
  errors: Object,
  user: Object,
})

// Default phone entry structure
const DEFAULT_PHONE = {
  id: null,
  owner: '',
  country_prefix: '',
  phone_number: '',
  phone_type: ''
}

// Computed properties for reactive data
const emails = computed(() => props.user.emails || [])
const phones = computed(() => {
  const userPhones = props.user.phones || []
  // Ensure at least one phone entry is shown
  if (userPhones.length === 0) {
    return [{ ...DEFAULT_PHONE }]
  }
  return userPhones
})
const phoneTypes = computed(() => props.phone_types || [])

// Check if there are any pending changes
const hasPendingChanges = computed(() => {
  // Check emails for pending changes
  const hasPendingEmails = emails.value.some(email => 
    (email.pending_address !== null && email.pending_address !== undefined) ||
    (email.pending_owner !== null && email.pending_owner !== undefined)
  )
  
  // Check phones for pending changes
  const hasPendingPhones = phones.value.some(phone => 
    (phone.pending_owner !== null && phone.pending_owner !== undefined) ||
    (phone.pending_country_prefix !== null && phone.pending_country_prefix !== undefined) ||
    (phone.pending_phone_number !== null && phone.pending_phone_number !== undefined) ||
    (phone.pending_phone_type !== null && phone.pending_phone_type !== undefined)
  )
  
  // Check physical address for pending changes
  const hasPendingPhysical = props.user.addresses?.physical && (
    (props.user.addresses.physical.pending_address !== null && props.user.addresses.physical.pending_address !== undefined) ||
    (props.user.addresses.physical.pending_city !== null && props.user.addresses.physical.pending_city !== undefined) ||
    (props.user.addresses.physical.pending_state !== null && props.user.addresses.physical.pending_state !== undefined) ||
    (props.user.addresses.physical.pending_zip !== null && props.user.addresses.physical.pending_zip !== undefined) ||
    (props.user.addresses.physical.pending_county !== null && props.user.addresses.physical.pending_county !== undefined) ||
    (props.user.addresses.physical.pending_country_id !== null && props.user.addresses.physical.pending_country_id !== undefined)
  )
  
  // Check mailing address for pending changes
  const hasPendingMailing = props.user.addresses?.mailing && (
    (props.user.addresses.mailing.pending_address !== null && props.user.addresses.mailing.pending_address !== undefined) ||
    (props.user.addresses.mailing.pending_city !== null && props.user.addresses.mailing.pending_city !== undefined) ||
    (props.user.addresses.mailing.pending_state !== null && props.user.addresses.mailing.pending_state !== undefined) ||
    (props.user.addresses.mailing.pending_zip !== null && props.user.addresses.mailing.pending_zip !== undefined) ||
    (props.user.addresses.mailing.pending_county !== null && props.user.addresses.mailing.pending_county !== undefined) ||
    (props.user.addresses.mailing.pending_country_id !== null && props.user.addresses.mailing.pending_country_id !== undefined) ||
    (props.user.addresses.mailing.pending_same_as_physical !== null && props.user.addresses.mailing.pending_same_as_physical !== undefined)
  )
  
  return hasPendingEmails || hasPendingPhones || hasPendingPhysical || hasPendingMailing
})

// Validation schema
const formSchema = toTypedSchema(z.object({
  emails: z.array(z.object({
    id: z.number().optional(),
    address: z.string().email('Please enter a valid email address'),
    owner: z.string().optional(),
    primary: z.boolean().optional()
  })),
  phones: z.array(z.object({
    id: z.number().optional().nullable(),
    owner: z.string().optional(),
    country_prefix: z.string().optional(),
    phone_number: z.string().optional(),
    phone_type: z.string().optional()
  }).superRefine((val, ctx) => {
    // Check if any field has data (excluding id)
    const hasAnyData = val.owner || val.country_prefix || val.phone_number || val.phone_type
    // If any field has data, phone_number becomes required
    if (hasAnyData && (!val.phone_number || val.phone_number.trim() === '')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Phone number is required',
        path: ['phone_number']
      })
    }
  })),
  addresses: z.object({
    mailing: z.object({
      address_type: z.string(),
      address: z.string().nullable().optional(),
      city: z.string().nullable().optional(),
      state: z.string().nullable().optional(),
      zip: z.string().nullable().optional(),
      county: z.string().nullable().optional(),
      country_id: z.number().optional().nullable(),
      same_as_physical: z.boolean().optional()
    }).superRefine(
      (val, ctx) => {
        if (!val.same_as_physical) {
          if (!val.address || val.address.trim() === '') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Address is required when not using physical address',
              path: ['address']
            })
          }
          if (!val.city || val.city.trim() === '') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'City is required when not using physical address',
              path: ['city']
            })
          }
          if (!val.state || val.state.trim() === '') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'State is required when not using physical address',
              path: ['state']
            })
          }
          if (!val.zip || val.zip.trim() === '') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'ZIP code is required when not using physical address',
              path: ['zip']
            })
          }
          if (!val.county || val.county.trim() === '') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'County is required when not using physical address',
              path: ['county']
            })
          }
          if (val.country_id === null || val.country_id === undefined) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Country is required when not using physical address',
              path: ['country_id']
            })
          }
        }
      }
    ),

    physical: z.object({
      address_type: z.string(),
      address: z.string().nullable(),
      city: z.string().nullable(),
      state: z.string().nullable(),
      zip: z.string().nullable(),
      county: z.string().nullable(),
      country_id: z.number().nullable(),
    }).superRefine(
      (val, ctx) => {
        // Get initial data from server
        const initialPhysical = props.user.addresses?.physical || {}
        
        // Helper function to check if a value should be required
        const shouldRequire = (currentValue, initialValue) => {
          // If initial value was null/empty, allow null/empty
          if (initialValue === null || initialValue === undefined || 
              (typeof initialValue === 'string' && initialValue.trim() === '')) {
            return false
          }
          // If initial value had data, require current value to have data
          return true
        }
        
        // Check string fields
        const stringFields = ['address', 'city', 'state', 'zip', 'county']
        stringFields.forEach(field => {
          const currentValue = val[field]
          const initialValue = initialPhysical[field]
          
          if (shouldRequire(currentValue, initialValue)) {
            if (!currentValue || currentValue.trim() === '') {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${field.charAt(0).toUpperCase() + field.slice(1)} is required`,
                path: [field]
              })
            }
          }
        })
        
        // Check country_id
        if (shouldRequire(val.country_id, initialPhysical.country_id)) {
          if (val.country_id === null || val.country_id === undefined) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Country is required',
              path: ['country_id']
            })
          }
        }
      }
    ),
  }),
}))

// Initialize form with user data
const initialValues = {
  emails: props.user.emails || [],
  phones: props.user.phones && props.user.phones.length > 0 ? props.user.phones : [{ ...DEFAULT_PHONE }],
  addresses: props.user.addresses || {
    physical: {
      address_type: 'physical',
      address: '',
      city: '',
      state: '',
      zip: '',
      county: '',
      country_id: null
    },
    mailing: {
      address_type: 'mailing',
      address: '',
      city: '',
      state: '',
      zip: '',
      county: '',
      country_id: null,
      same_as_physical: false
    }
  }
}

// If user has actual data, use it instead of defaults
if (props.user.addresses) {
  if (props.user.addresses.physical) {
    initialValues.addresses.physical = { ...initialValues.addresses.physical, ...props.user.addresses.physical }
  }
  if (props.user.addresses.mailing) {
    initialValues.addresses.mailing = { 
      ...initialValues.addresses.mailing, 
      ...props.user.addresses.mailing,
      // Ensure same_as_physical is always a proper boolean
      same_as_physical: Boolean(props.user.addresses.mailing.same_as_physical)
    }
  }
}

const { handleSubmit, values, errors: formErrors, setFieldValue, setFieldError } = useVeeForm({
  validationSchema: formSchema,
  initialValues
})

// Computed property for showing/hiding mailing address fields
const showMailingAddressFields = computed(() => {
  return !values.addresses?.mailing?.same_as_physical
})

// Computed property to check if country is USA
const isUSACountry = (countryId) => {
  const usaCountry = props.countries.find(c => c.name === 'USA')
  return usaCountry && countryId === usaCountry.id
}

// Track whether county should be manually editable (due to failed lookup)
const countyManualEdit = ref({
  physical: false,
  mailing: false
})

// Computed properties for readonly county fields
const isPhysicalCountyReadonly = computed(() => {
  // If manual edit is enabled, allow editing
  if (countyManualEdit.value.physical) return false
  // Otherwise, readonly for USA addresses (auto-populated)
  return isUSACountry(values.addresses?.physical?.country_id)
})

const isMailingCountyReadonly = computed(() => {
  // If manual edit is enabled, allow editing
  if (countyManualEdit.value.mailing) return false
  // Otherwise, readonly for USA addresses (auto-populated)
  return isUSACountry(values.addresses?.mailing?.country_id)
})

// Function to lookup county by zipcode
const lookupCountyByZipcode = async (zipcode, addressType) => {
  if (!zipcode || zipcode.length !== 5) return

  // Only lookup for USA
  const countryId = addressType === 'physical' 
    ? values.addresses?.physical?.country_id 
    : values.addresses?.mailing?.country_id

  const usaCountry = props.countries.find(c => c.name === 'USA')
  if (!usaCountry || countryId !== usaCountry.id) return

  // Reset manual edit flag on new lookup
  countyManualEdit.value[addressType] = false

  try {
    const response = await fetch(`/api/zipcodes/lookup?zipcode=${zipcode}`)
    if (response.ok) {
      const data = await response.json()
      if (data.county_name) {
        setFieldValue(`addresses.${addressType}.county`, data.county_name)
      }
    } else if (response.status === 404) {
      // Zipcode not found in database - enable manual edit
      setFieldValue(`addresses.${addressType}.county`, '')
      countyManualEdit.value[addressType] = true
      toast({
        title: 'Zipcode not found',
        description: `Please enter the county manually.`,
        duration: 5000,
      })
    } else {
      // Other error - enable manual edit
      countyManualEdit.value[addressType] = true
      const data = await response.json()
      toast({
        title: 'Error',
        description: data.error || 'Failed to lookup zipcode. Please enter the county manually.',
        variant: 'destructive',
        duration: 5000,
      })
    }
  } catch (error) {
    // Exception - enable manual edit
    console.error('Failed to lookup zipcode:', error)
    countyManualEdit.value[addressType] = true
    toast({
      title: 'Error',
      description: 'An unexpected error occurred while looking up the zipcode. Please enter the county manually.',
      variant: 'destructive',
      duration: 5000,
    })
  }
}

// Watch for changes to same_as_physical checkbox
watch(() => values.addresses?.mailing?.same_as_physical, (newValue) => {
  if (newValue) {
    // Clear mailing address fields when same_as_physical is checked
    setFieldValue('addresses.mailing.address', null)
    setFieldValue('addresses.mailing.city', null)
    setFieldValue('addresses.mailing.state', null)
    setFieldValue('addresses.mailing.zip', null)
    setFieldValue('addresses.mailing.county', null)
    setFieldValue('addresses.mailing.country_id', null)
  }
})

// Debounce timers for zipcode lookup
const physicalZipDebounce = ref(null)
const mailingZipDebounce = ref(null)

// Watch for changes to physical address zipcode
watch(() => values.addresses?.physical?.zip, (newZip) => {
  // Clear existing timeout
  if (physicalZipDebounce.value) {
    clearTimeout(physicalZipDebounce.value)
    physicalZipDebounce.value = null
  }
  
  // Only lookup if we have exactly 5 digits
  if (newZip && newZip.length === 5 && /^\d{5}$/.test(newZip)) {
    // Debounce the lookup by 500ms
    physicalZipDebounce.value = setTimeout(() => {
      lookupCountyByZipcode(newZip, 'physical')
    }, 500)
  }
})

// Watch for changes to mailing address zipcode
watch(() => values.addresses?.mailing?.zip, (newZip) => {
  // Clear existing timeout
  if (mailingZipDebounce.value) {
    clearTimeout(mailingZipDebounce.value)
    mailingZipDebounce.value = null
  }
  
  // Only lookup if we have exactly 5 digits and not using physical address
  if (newZip && newZip.length === 5 && /^\d{5}$/.test(newZip) && !values.addresses?.mailing?.same_as_physical) {
    // Debounce the lookup by 500ms
    mailingZipDebounce.value = setTimeout(() => {
      lookupCountyByZipcode(newZip, 'mailing')
    }, 500)
  }
})

// Function to set primary email
const setPrimaryEmail = (selectedIndex) => {
  // Set all emails to non-primary first
  values.emails.forEach((email, index) => {
    setFieldValue(`emails.${index}.primary`, false)
  })

  // Set the selected email as primary
  setFieldValue(`emails.${selectedIndex}.primary`, true)
}

// Inertia form for submission
const form = useForm(initialValues)

// Submit handler
const onSubmit = handleSubmit(
  async validatedValues => {
    Object.assign(form, validatedValues)

    form.put('/profile')
  }
)
</script>
